import type { ResolvedTuningConfiguration, TuningCapabilities, TuningSettingReceipt } from '@playcastdotio/PeerCore';

/** Only public negotiation APIs and bounded SDP policy; no browser decoder internals. */
export class GuestTrialNegotiation {
  private configuration?: ResolvedTuningConfiguration;
  private readonly receipts: TuningSettingReceipt[] = [];
  configure(configuration: ResolvedTuningConfiguration): void { this.configuration = configuration; this.receipts.length = 0; }
  settings(): readonly TuningSettingReceipt[] { return [...this.receipts]; }
  preflight(configuration: ResolvedTuningConfiguration, connection: RTCPeerConnection | null): void {
    const requested = configuration.settings['video.codec'];
    if (!requested || requested === 'auto') return;
    if (!this.codecs().some(codec => codec.mimeType.toLowerCase() === `video/${requested}`)
      || !connection?.getTransceivers().some(transceiver => typeof transceiver.setCodecPreferences === 'function')) {
      throw new Error(`Requested ${requested} codec preferences are unavailable`);
    }
  }
  capabilities(): TuningCapabilities['settings'] {
    const codecs = this.codecs();
    return { 'video.codec': { mode: 'fresh-stream', scope: 'peer', nativeApi: codecs.length ? 'supported' : 'unknown',
      values: ['auto', ...(['h264', 'h265'] as const).filter(codec => codecs.some(entry => entry.mimeType.toLowerCase() === `video/${codec}`))] },
      'video.nackRtx': { mode: 'fresh-stream', scope: 'peer', nativeApi: 'supported', values: ['default', 'disabled'] },
      'ice.transportPolicy': { mode: 'fresh-stream', scope: 'peer', nativeApi: 'supported', values: ['all', 'relay'] },
      'audio.stereo': { mode: 'fresh-stream', scope: 'peer', nativeApi: 'supported', values: [true, false] },
      'audio.packetTimeMs': { mode: 'fresh-stream', scope: 'peer', nativeApi: 'supported', values: [10, 20] } };
  }
  apply(connection: RTCPeerConnection): void {
    const codec = this.configuration?.settings['video.codec'];
    if (!codec || codec === 'auto') return;
    const selected = this.codecs().filter(entry => entry.mimeType.toLowerCase() === `video/${codec}`);
    const video = connection.getTransceivers().filter(entry => entry.receiver.track.kind === 'video');
    if (!selected.length || !video.length || video.some(entry => typeof entry.setCodecPreferences !== 'function')) throw new Error(`Requested ${codec} codec preferences are unavailable`);
    const repair = this.configuration?.settings['video.nackRtx'] === 'disabled' ? []
      : this.codecs().filter(entry => /video\/(rtx|red|ulpfec|flexfec)/i.test(entry.mimeType));
    for (const transceiver of video) transceiver.setCodecPreferences([...selected, ...repair]);
    this.receipts.push({ key: 'video.codec', requested: codec, accepted: codec, status: 'applied', reason: 'Offered codec preference; negotiated codec is reported separately' });
  }
  transform(sdp: string): string {
    const settings = this.configuration?.settings;
    if (!settings) return sdp;
    return sdp.split(/(?=m=)/).map(section => {
      if (section.startsWith('m=video') && settings['video.nackRtx'] === 'disabled') {
        const rtx = [...section.matchAll(/^a=rtpmap:(\d+) rtx\/\d+\r?$/gm)].map(match => match[1]);
        const lines = section.split('\r\n').filter(line => !/^a=rtcp-fb:\S+ nack$/.test(line)
          && !rtx.some(payload => new RegExp(`^a=(?:rtpmap|fmtp|rtcp-fb):${payload}(?: |$)`).test(line)));
        lines[0] = lines[0].split(' ').filter((token, index) => index < 3 || !rtx.includes(token)).join(' ');
        return lines.join('\r\n');
      }
      if (section.startsWith('m=audio')) {
        if (typeof settings['audio.packetTimeMs'] === 'number') section = section.replace(/^a=(?:p|maxp)time:.*\r?\n/gm, '') + `a=ptime:${settings['audio.packetTimeMs']}\r\n`;
        if (typeof settings['audio.stereo'] === 'boolean') section = section.replace(/^(a=fmtp:\d+ .*?)(?:\r?$)/gm, (line) => {
          if (!/useinbandfec=|stereo=|minptime=/.test(line)) return line;
          return line.replace(/;?(?:sprop-)?stereo=\d+/g, '') + `;stereo=${settings['audio.stereo'] ? 1 : 0}`;
        });
      }
      return section;
    }).join('');
  }
  private codecs(): RTCRtpCodec[] {
    try { return RTCRtpReceiver.getCapabilities('video')?.codecs ?? []; } catch { return []; }
  }
}
